/**
 * fileValidation.ts
 * 
 * Utility functions for validating file types and sizes.
 * Provides centralized constants and validation methods for file handling.
 */

import { FileAttachment } from '@/types/posit';

// Maximum file sizes
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB for general uploads
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB for image uploads

// File type categories
export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

export const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.google-apps.document',
];

export const SPREADSHEET_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.google-apps.spreadsheet',
];

// Combined list of all supported file types
export const SUPPORTED_MIME_TYPES = [
  ...IMAGE_MIME_TYPES,
  ...DOCUMENT_MIME_TYPES,
  ...SPREADSHEET_MIME_TYPES,
];

/**
 * Checks if a file type is supported
 * @param mimeType The MIME type to check
 * @returns boolean indicating if the file type is supported
 */
export const isFileTypeSupported = (mimeType: string): boolean => {
  return SUPPORTED_MIME_TYPES.includes(mimeType);
};

/**
 * Checks if a file size is within the allowed limit
 * @param size The file size in bytes
 * @param isImage Whether the file is an image (uses stricter size limit)
 * @returns boolean indicating if the file size is acceptable
 */
export const isFileSizeValid = (size: number, isImage: boolean = false): boolean => {
  const maxSize = isImage ? MAX_IMAGE_SIZE : MAX_FILE_SIZE;
  return size <= maxSize;
};

/**
 * Gets the file type category based on MIME type
 * @param mimeType The MIME type to categorize
 * @returns The category of the file ('image', 'document', 'spreadsheet', or 'unknown')
 */
export const getFileTypeCategory = (mimeType: string): 'image' | 'document' | 'spreadsheet' | 'unknown' => {
  if (IMAGE_MIME_TYPES.includes(mimeType)) return 'image';
  if (DOCUMENT_MIME_TYPES.includes(mimeType)) return 'document';
  if (SPREADSHEET_MIME_TYPES.includes(mimeType)) return 'spreadsheet';
  return 'unknown';
};

/**
 * Validates a file attachment
 * @param file The file attachment to validate
 * @returns An object with validation results
 */
export const validateFileAttachment = (file: FileAttachment): { 
  valid: boolean; 
  error?: string;
  category: 'image' | 'document' | 'spreadsheet' | 'unknown';
} => {
  const category = getFileTypeCategory(file.mimeType);
  
  if (category === 'unknown') {
    return {
      valid: false,
      error: `Unsupported file type: ${file.mimeType}`,
      category
    };
  }
  
  if (file.size && !isFileSizeValid(file.size, category === 'image')) {
    const maxSize = category === 'image' ? MAX_IMAGE_SIZE : MAX_FILE_SIZE;
    return {
      valid: false,
      error: `File size exceeds the ${maxSize / (1024 * 1024)}MB limit`,
      category
    };
  }
  
  return {
    valid: true,
    category
  };
};

/**
 * Validates a File object
 * @param file The File object to validate
 * @returns An object with validation results
 */
export const validateFile = (file: File): { 
  valid: boolean; 
  error?: string;
  category: 'image' | 'document' | 'spreadsheet' | 'unknown';
} => {
  const category = getFileTypeCategory(file.type);
  
  if (category === 'unknown') {
    return {
      valid: false,
      error: `Unsupported file type: ${file.type}`,
      category
    };
  }
  
  if (!isFileSizeValid(file.size, category === 'image')) {
    const maxSize = category === 'image' ? MAX_IMAGE_SIZE : MAX_FILE_SIZE;
    return {
      valid: false,
      error: `File size exceeds the ${maxSize / (1024 * 1024)}MB limit`,
      category
    };
  }
  
  return {
    valid: true,
    category
  };
}; 